import { Component, ErrorInfo, FC, ReactNode, Suspense } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Alert, Stack, Typography } from '@mui/material';

import { Button, Loading } from '@components/base';

type ErrorBoundaryState = {
  error?: Error;
  hasError: boolean;
};

type ErrorBoundaryProps = {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
} & WithTranslation;

class ChunkErrorBoundaryComponent extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.props.onError?.(error, errorInfo);
  }

  isChunkError = (error?: Error): boolean =>
    Boolean(
      error?.message?.includes('Failed to fetch dynamically imported module') ||
        error?.message?.includes('Loading chunk')
    );

  handleReload = () => window.location.reload();

  render() {
    const { hasError, error } = this.state;
    const { children, t } = this.props;

    if (hasError && error) {
      const isChunkError = this.isChunkError(error);

      return (
        <Stack p={2} spacing={2}>
          <Alert severity={isChunkError ? 'warning' : 'error'}>
            <Typography gutterBottom variant='h6'>
              {isChunkError ? t('suspense.loadingIssue') : t('suspense.error')}
            </Typography>

            <Typography variant='body2'>
              {isChunkError ? t('suspense.chunkErrorMessage') : error.message}
            </Typography>
          </Alert>

          <Stack direction='row' spacing={2}>
            <Button onClick={this.handleReload} size='small' variant='contained'>
              {t('suspense.reload')}
            </Button>
          </Stack>
        </Stack>
      );
    }

    return children;
  }
}

const ChunkErrorBoundary = withTranslation()(ChunkErrorBoundaryComponent);

type RobustSuspenseProps = {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
};

export const RobustSuspense: FC<RobustSuspenseProps> = ({
  children,
  fallback = <Loading />,
  onError,
}) => (
  <ChunkErrorBoundary onError={onError}>
    <Suspense fallback={fallback}>{children}</Suspense>
  </ChunkErrorBoundary>
);
